import { useAuth0 } from "@auth0/auth0-react";
import heic2any from "heic2any";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Constants from "../../components/Constants";
import { callExternalApi, postExternalApi } from "../../components/external-api.service";
import * as LocalCache from "../../components/LocalCache";
import { MAX_RETRIES_FOR_IMAGE_SUBMIT, RETRY_DELAY } from "../../settings";

export const useEdit = () => {

    const [recipe, setRecipe] = useState(null);
    const [imageFileToUpload, setImageFileToUpload] = useState(null); // binary image, of 'File' type
    const [newImage, setNewImage] = useState(null);
    const [whyRecipeIsInvalid, setWhyRecipeIsInvalid] = useState("");

    // UI state
    const [userHasMadeEdits, setUserHasMadeEdits] = useState(false);
    const [userHasEditedBody, setUserHasEditedBody] = useState(false);
    const [saveModalIsActive, setSaveModalIsActive] = useState(false);
    const [invalidInputModalIsActive, setInvalidInputModalIsActive] =
        useState(false);
    const [imageSelectorModalIsActive, setImageSelectorModalIsActive] =
        useState(false);
    const [modalErrorMessage, setModalErrorMessage] = useState(null);

    var { guid } = useParams();
    const recipe_is_new = guid == "new";

    // avoid calling loadRecipeDetail() 2x on component load.
    const alreadyCalledLoadRecipeDetail = useRef(false);

    const [userOwnsThisRecipe, setUserOwnsThisRecipe] = useState(false);

    const navigate = useNavigate();

    const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();

    function getUpdateOrNewMessage() {
        return recipe_is_new ? "Recipe created." : "Recipe updated.";
    }

    const loadRecipeDetail = async () => {
        if (alreadyCalledLoadRecipeDetail.current) {
            return;
        }
        alreadyCalledLoadRecipeDetail.current = true;

        if (recipe_is_new) {
            const newrecipe = {
                recipe_name: "",
                recipe_body: "",
                visibility: Constants.VISIBILITY_PUBLIC,
            };
            setRecipe(newrecipe);
            return;
        }
        const encodedUser = Constants.encodeUser(user);
        const url = Constants.generateSecureApiUrl(
            Constants.APIG_GET_RESOURCE,
            "user=" + encodedUser + "&guid=" + guid
        );
        const accessToken = await Constants.getAccessTokenSilentlyWithTimeout(
            getAccessTokenSilently,
            loginWithRedirect,
            navigate
        );
        const { data, error } = await callExternalApi(url, accessToken);
        if (error) {
            if (error.code === "RECIPE_NOT_FOUND") {
                setModalErrorMessage(
                    "That recipe does not exist or has recently been deleted."
                );
            } else {
                setModalErrorMessage("Failed to load recipe.");
            }
            return;
        }
        // Defensive fallback for legacy backend response shape during rollout.
        if (data && data.recipe_name == "**ITEM NOT FOUND**") {
            setModalErrorMessage(
                "That recipe does not exist or has recently been deleted."
            );
            return;
        }

        // fill missing fields with defaults, etc.
        const r = Constants.trueUpRecipe(data);

        setRecipe(r);

        if (data.userid == encodedUser) {
            setUserOwnsThisRecipe(true);
        }
    };

    const submit = async () => {
        const encodedUser = Constants.encodeUser(user);
        const accessToken = await Constants.getAccessTokenSilentlyWithTimeout(
            getAccessTokenSilently,
            loginWithRedirect,
            navigate
        );

        // sanity checks on recipe
        const ri = recipe.recipe_body.trim();
        const rname = recipe.recipe_name;
        if (rname.length < Constants.MIN_RECIPE_NAME_LENGTH) {
            setInvalidInputModalIsActive(true);
            setWhyRecipeIsInvalid(
                "Recipe name must be at least " +
                Constants.MIN_RECIPE_NAME_LENGTH +
                " characters in length."
            );
            return;
        }
        if (ri.length < Constants.MIN_RECIPE_BODY_LENGTH) {
            setInvalidInputModalIsActive(true);
            setWhyRecipeIsInvalid(
                "Recipe must be at least " +
                Constants.MIN_RECIPE_BODY_LENGTH +
                " characters in length."
            );
            return;
        }

        setSaveModalIsActive(!saveModalIsActive);

        // looks good. Submit. We first submit the text, then (assuming success) the image, in a separate POST.

        // For an existing recipe, guid was passed into this component as a param. For a new recipe, we generate a new guid here.
        if (recipe_is_new) {
            guid = Constants.generateGUID();
        }

        // step 1: submit the text
        const url = Constants.generateSecureApiUrl(
            Constants.APIG_UPDATE_RESOURCE,
            "mode=text" + "&guid=" + guid + "&user=" + encodedUser
        );
        var payload = {
            recipe_body: _b64(recipe.recipe_body),
            recipe_name: _b64(recipe.recipe_name),
            category: _b64(recipe.category),
            visibility: _b64(Constants.VISIBILITY_PUBLIC),
            notes: _b64(recipe.notes), 
        };
        if (!userHasEditedBody) {
            delete payload.recipe_body; // don't upload recipe body if it has not changed
        }

        // Store recipe data in local cache, so Browse can use the fresh data for the recipe, vs. relying on stale Algolia data.
        try {
            LocalCache.add(guid, LocalCache.EDIT_STATUS, rname, imageFileToUpload);
        } catch (error) {
            console.error("Error adding/updating item in IndexedDB:", error);
        }

        const { error } = await postExternalApi(url, payload, accessToken);
        if (error) {
            console.log("Error uploading recipe: " + error.message);
            setSaveModalIsActive(false);
            setModalErrorMessage("Failed to save recipe. Please try again.");
            return;
        }

        // step 2: submit the image, if there is one.
        if (imageFileToUpload) {
            submitImagePart();
        }
    };

    function _b64(str) {
        // 1. Use encodeURIComponent to get percent-encoded UTF-8 string
        const utf8String = encodeURIComponent(str);
        // 2. Convert the percent-encoded string into an actual byte string
        const byteString = utf8String.replace(/%([0-9A-F]{2})/g, function (_, p1) {
            return String.fromCharCode("0x" + p1);
        });
        // 3. Use btoa to encode the byte string to base64
        const base64String = btoa(byteString);
        return base64String;
    }


    const submitImagePart = async (retryCount = 0) => {

        const accessToken = await Constants.getAccessTokenSilentlyWithTimeout(
            getAccessTokenSilently,
            loginWithRedirect,
            navigate
        );
        const encodedUser = Constants.encodeUser(user);
        setSaveModalIsActive(!saveModalIsActive);
        const url = Constants.generateSecureApiUrl(
            Constants.APIG_UPDATE_RESOURCE,
            "mode=image" + "&guid=" + guid + "&user=" + encodedUser
        );

        const reader = new FileReader();
        reader.onloadend = async () => {
            const arr = new Uint8Array(reader.result).subarray(0, 4);
            let header = "";
            for (let i = 0; i < arr.length; i++) {
                header += arr[i].toString(16);
            }
            const mimeType = Constants.getImageMimeTypeFromHeader(header);
            if (mimeType === "") {
                console.log("Unrecognized mime type");
            } else {
                const { error } = await postExternalApi(url, imageFileToUpload, accessToken, mimeType);
                if (error) {
                    console.log("Error submitting image part:", error.message);
                    if (retryCount < MAX_RETRIES_FOR_IMAGE_SUBMIT) {
                        console.log(`Retrying... Attempt ${retryCount + 1}`);
                        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
                        submitImagePart(retryCount + 1);
                    } else {
                        console.log("Max retries reached. Aborting.");
                    }
                }
            }
        };
        reader.readAsArrayBuffer(imageFileToUpload); // reading as binary
    };

    function useSelectedImage() {
        toggleImageSelectorModal();
    }

    function cancelSelectedImage() {
        toggleImageSelectorModal();
        setImageFileToUpload(null);
    }

    function toggleImageSelectorModal() {
        setImageSelectorModalIsActive(!imageSelectorModalIsActive);
    }

    // Visibility toggle removed: visibility is always public now

    useEffect(() => {
        let isMounted = true;
        const loadDataWrapper = async () => {
            const accessToken = await Constants.getAccessTokenSilentlyWithTimeout(
                getAccessTokenSilently,
                loginWithRedirect,
                navigate
            );
            await loadRecipeDetail(accessToken);
            if (!isMounted) {
                return;
            }
        };
        loadDataWrapper();
        return () => {
            isMounted = false;
        };
    }, [getAccessTokenSilently]);


    async function convertHeicToJpeg(heicFile) {
        try {
          const jpegBlob = await heic2any({
            blob: heicFile,
            toType: "image/jpeg",
            quality: 0.8
          });
          return jpegBlob;
        } catch (error) {
          console.error('Conversion failed:', error);
        }
      }


      const handleImageFileChange = async (event) => {
        console.log("handleImageFileChange start");
        setUserHasMadeEdits(true);
        
        const file = event.target.files[0];
        if (!file) return;
        
        try {
            let imageFile = file;
    
            // If HEIC, convert it to JPEG first
            if (file.type === 'image/heic' || file.name.endsWith('.heic')) {
                console.log("Converting HEIC to JPEG...");
                const jpegBlob = await convertHeicToJpeg(file);
    
                if (!jpegBlob) {
                  setModalErrorMessage("Sorry, I can't (yet) handle native HEIC images from an iphone. As a workaround, you can (1) take a screenshot of the image and upload that, or (2) email the image to yourself and use that image.")
                  return
                }              
    
                imageFile = new File([jpegBlob], file.name.replace(/\.heic$/, ".jpg"), {
                    type: "image/jpeg",
                    lastModified: Date.now(),
                });
    
                console.log("HEIC conversion complete");
            }
    
            // Resize the image
            const resizedFile = await Constants.resizeImage(imageFile);
            setImageFileToUpload(resizedFile);
    
            // Convert resized image to Data URL for preview
            const reader = new FileReader();
            reader.onload = (e) => setNewImage(e.target.result);
            reader.readAsDataURL(resizedFile);
        } catch (err) {
            console.error("Error processing image:", err);
        }
    };
        

    const handleChangeInName = (event) => {
        setUserHasMadeEdits(true);
        // force the input to be N characters or less for the title
        let input = event.target.value;
        input = input.replace(/\n/g, ""); // remove newlines
        if (input.length > Constants.MAX_RECIPE_NAME_LENGTH) {
            input = input.substring(0, Constants.MAX_RECIPE_NAME_LENGTH);
        }
        const updatedRecipe = { ...recipe, recipe_name: input };
        setRecipe(updatedRecipe);
    };

    const handleChangeInIngredients = (event) => {
        setUserHasMadeEdits(true);
        setUserHasEditedBody(true);
        let input = event.target.value;
        if (input.length > Constants.MAX_RECIPE_INGREDIENTS_LENGTH) {
            input = input.substring(0, Constants.MAX_RECIPE_INGREDIENTS_LENGTH);
        }
        const instructions = Constants.extractInstructions(recipe.recipe_body)
        const new_recipe_body = "Ingredients\n\n" + input + "\n\nInstructions:\n\n" + instructions.join("\n")
        const updatedRecipe = { ...recipe, recipe_body: new_recipe_body };
        setRecipe(updatedRecipe);
    };

    const handleChangeInInstructions = (event) => {
        setUserHasMadeEdits(true);
        setUserHasEditedBody(true);
 
        let input = event.target.value;
        if (input.length > Constants.MAX_RECIPE_INSTRUCTIONS_LENGTH) {
            input = input.substring(0, Constants.MAX_RECIPE_INSTRUCTIONS_LENGTH);
        }
        const ingredients = Constants.extractIngredients(recipe.recipe_body)
        const new_recipe_body = "Ingredients\n\n" + ingredients.join("\n") + "\n\nInstructions:\n\n" + input
        const updatedRecipe = { ...recipe, recipe_body: new_recipe_body };
        setRecipe(updatedRecipe);
    };

    const handleChangeInNotes = (event) => {
        if (!recipe) return;  // Prevents errors if recipe is not yet loaded
        setUserHasMadeEdits(true);
        let input = event.target.value;
        if (input.length > Constants.MAX_RECIPE_NOTES_LENGTH) {
            input = input.substring(0, Constants.MAX_RECIPE_NOTES_LENGTH);
        }
        const updatedRecipe = { ...recipe, notes: input };

        setRecipe(updatedRecipe);
    };

    return {
        modalErrorMessage,
        setModalErrorMessage,
        recipe,
        recipe_is_new,
        handleChangeInName,
        setRecipe,
        setUserHasMadeEdits,
        handleChangeInIngredients,
        handleChangeInInstructions,
        handleChangeInNotes,
        handleImageFileChange,
        submit,
        
        userOwnsThisRecipe,
        userHasMadeEdits,
        navigate,
        newImage,
        saveModalIsActive,
        getUpdateOrNewMessage,
        setSaveModalIsActive,
        invalidInputModalIsActive,
        whyRecipeIsInvalid,
        setInvalidInputModalIsActive,
        imageSelectorModalIsActive,
        imageFileToUpload,
        useSelectedImage,
        cancelSelectedImage,
    }
}